import { Component, Input, OnInit } from '@angular/core';
import { StoragePeriodTypeConstants } from 'angular/constants/StoragePeriodTypeConstants';
import { ProgressiveInputCallbackAndValue } from 'angular/custom/directives/progressive-input.directive';
import { ErrorHandlerService } from 'angular/custom/services/error-handler.service';
import { Growl } from 'angular/custom/services/growl.service';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { TogglePanelCallback } from '../custom/components/toggle-panel.component';
import { SsoSettingsResource } from '../resources/sso-settings.resource';
import { AuthService } from 'app/_services/auth.service';

@Component({
    selector: 'pkp-storage-advanced-settings',
    template: require('./storage-advanced-settings.component.html')
})
export class StorageAdvancedSettingsComponent implements OnInit {
    @Input() ssoRefId: number;

    isOpen: boolean;
    advancedSettings: StorageAdvancedSettingsDto;
    newMinBidQuantities: StorageMinQuantityDto;
    /** The quantity that's currently being edited in its unchanged state to return to it in case of cancel. */
    originalMinQuantitiesBeingEdited: StorageMinQuantityDto[] = [];
    loadingAdvancedSettings: boolean = true;
    savingMinQuantity: boolean | number = false;
    deletingMinQuantity: number;
    readonly allPeriodTypes: {
        [key in StoragePeriodType]: string;
    } = StoragePeriodTypeConstants.asDict();
    readonly storageQuantityUnit = {
        SBU: 'SBU',
        KWH: 'kWh',
        KWH_H: 'kWh/h',
        KWH_D: 'kWh/d',
        M3: 'm³',
        M3_H: 'm³/h'
    };
    manualAllocationTriggerSettingDisabled: boolean;

    constructor(
        private ssoSettingsResource: SsoSettingsResource,
        private authService: AuthService,
        private growl: Growl,
        private errorHandlerService: ErrorHandlerService
    ) {}

    ngOnInit(): void {
        this.loadingAdvancedSettings = true;

        forkJoin([
            this.ssoSettingsResource.fetchAdvancedSettings(this.ssoRefId),
            this.ssoSettingsResource.queryAllocationServiceSubscription(
                this.ssoRefId
            )
        ])
            .pipe(finalize(() => (this.loadingAdvancedSettings = false)))
            .subscribe((res) => {
                this.isOpen = res[0] != null;
                res[0] != null && (this.advancedSettings = res[0]);
                this.manualAllocationTriggerSettingDisabled =
                    res[1] == null || res[1].servicePackage === 'NO_ALGORITHM';
            });
    }

    toggleAdvancedSettings(cb: TogglePanelCallback) {
        this.ssoSettingsResource
            .toggleAdvancedSettings(this.ssoRefId, this.isOpen)
            .subscribe(
                (res) => {
                    cb(true);
                    if (this.isOpen) {
                        this.growl.show(
                            'Successfully enabled settings',
                            'success'
                        );
                        this.advancedSettings = res;
                    } else {
                        this.growl.show(
                            'Successfully disabled settings',
                            'success'
                        );
                    }

                    if (this.isOpen && !this.advancedSettings) {
                        this.advancedSettings = {
                            maxBidsPerShipper: null,
                            minBidQuantities: [],
                            upgradePeriodTypes: [],
                            manualAllocationPeriodTypes: []
                        };
                    }
                },
                (err) => {
                    cb(false);
                    this.errorHandlerService.handle(err);
                }
            );
    }

    saveMaximumNumberOfBids(e: ProgressiveInputCallbackAndValue) {
        const value = parseInt(e.value);
        if (isNaN(value) || value < 0) {
            this.growl.show('Please enter an unsigned integer.', 'danger');
            e.cb(false);
            return;
        }
        this.ssoSettingsResource
            .saveMaxBidsPerShipper(this.ssoRefId, value)
            .subscribe(
                (savedMaxBidsPerShipper) => {
                    e.cb(true);
                    this.advancedSettings.maxBidsPerShipper =
                        savedMaxBidsPerShipper;
                    this.growl.show('Successfully saved.', 'success');
                },
                (err) => {
                    e.cb(false);
                    this.errorHandlerService.handle(err);
                }
            );
    }

    addMinimumBidQuantityPeriod() {
        this.newMinBidQuantities = {
            minQuantity: {} as StorageQuantityDto,
            periodType: null
        } as StorageMinQuantityDto;
    }

    saveMinQuantity(id?: number) {
        this.savingMinQuantity = id == null ? true : id;
        const minQuantityToSave: StorageMinQuantityDto =
            id == null
                ? this.newMinBidQuantities
                : this.advancedSettings.minBidQuantities.find(
                      (mQ) => mQ.id === id
                  );
        this.ssoSettingsResource
            .saveMinQuantity(this.ssoRefId, minQuantityToSave)
            .pipe(finalize(() => (this.savingMinQuantity = false)))
            .subscribe((res: StorageMinQuantityDto) => {
                if (id == null) {
                    this.advancedSettings.minBidQuantities.reverse().push(res);
                    this.advancedSettings.minBidQuantities.reverse();
                    this.newMinBidQuantities = null;
                } else {
                    this.advancedSettings.minBidQuantities[
                        this.advancedSettings.minBidQuantities.findIndex(
                            (mQ) => mQ.id === id
                        )
                    ] = res;
                    this.originalMinQuantitiesBeingEdited.splice(
                        this.originalMinQuantitiesBeingEdited.findIndex(
                            (mQ) => mQ.id === id
                        ),
                        1
                    );
                }
                this.growl.show('Successfully saved.', 'success');
            }, this.errorHandlerService.handle.bind(this));
    }

    deleteMinQuantity(id: number) {
        this.deletingMinQuantity = id;
        this.ssoSettingsResource
            .deleteMinQuantity(this.ssoRefId, id)
            .pipe(finalize(() => (this.deletingMinQuantity = null)))
            .subscribe(() => {
                this.advancedSettings.minBidQuantities.splice(
                    this.advancedSettings.minBidQuantities.findIndex(
                        (mQ) => mQ.id === id
                    ),
                    1
                );
                this.growl.show('Successfully deleted.', 'success');
            }, this.errorHandlerService.handle.bind(this));
    }

    editMinQuantity(id: number) {
        this.originalMinQuantitiesBeingEdited.push(
            JSON.parse(
                JSON.stringify(
                    this.advancedSettings.minBidQuantities.find(
                        (mQ) => mQ.id === id
                    )
                )
            )
        );
    }

    cancelMinQuantityEdit(id: number) {
        const idxOfOriginalQuantity =
            this.originalMinQuantitiesBeingEdited.findIndex(
                (mQ) => mQ.id === id
            );
        this.advancedSettings.minBidQuantities[
            this.advancedSettings.minBidQuantities.findIndex(
                (mQ) => mQ.id === id
            )
        ] = this.originalMinQuantitiesBeingEdited[idxOfOriginalQuantity];
        this.originalMinQuantitiesBeingEdited.splice(idxOfOriginalQuantity, 1);
    }

    isBeingEdited(id: number) {
        return (
            this.originalMinQuantitiesBeingEdited.findIndex(
                (mQ) => mQ.id === id
            ) !== -1
        );
    }

    public saveProductPeriodForUpgrade(periodType: StoragePeriodType) {
        return this.ssoSettingsResource.saveProductPeriodForUpgrade(periodType);
    }

    public deleteProductPeriodForUpgrade(periodType: StoragePeriodType) {
        return this.ssoSettingsResource.deleteProductPeriodForUpgrade(
            periodType
        );
    }

    public saveProductPeriodForManualAllocation(periodType: StoragePeriodType) {
        return this.ssoSettingsResource.saveProductPeriodForManualAllocation(
            periodType
        );
    }

    public deleteProductPeriodForManualAllocation(
        periodType: StoragePeriodType
    ) {
        return this.ssoSettingsResource.deleteProductPeriodForManualAllocation(
            periodType
        );
    }
}
